import { useI18n } from '../i18n/i18n';
import { useSEO } from '../hooks/useSEO';
import { 
  LineChart, 
  Bot, 
  Target, 
  Users, 
  Linkedin, 
  Github, 
  Globe,
  Sparkles,
  Shield,
  Zap,
  Heart,
  TrendingUp,
  Calculator,
  PiggyBank,
  ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useCurrentLanguage } from '../components/LanguageRedirect';

// Team member data with social links
const teamMembers = [
  {
    id: 'roberto_ruiz',
    image: '/images/team/roberto-ruiz.jpg',
    linkedin: 'https://www.linkedin.com/in/roberto-ruiz-sanchez/',
    github: 'https://github.com/rruisan',
    portfolio: 'https://rruisan-portfolio.netlify.app/',
    skillKeys: ['python', 'deepLearning', 'dataScience', 'machineLearning', 'aws', 'azure']
  },
  {
    id: 'roberto_otero',
    image: '/images/team/roberto-otero.jpg',
    linkedin: 'https://www.linkedin.com/in/roberto-otero-galan/',
    github: 'https://github.com/roberottt',
    portfolio: 'https://robertooterogalan.vercel.app/',
    skillKeys: ['java', 'python', 'ai', 'docker', 'cybersecurity']
  }
];

const AboutPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const currentLang = useCurrentLanguage();

  // SEO Configuration
  const seoContent = useSEO('main', 'about');

  const values = [
    { icon: Shield, key: 'transparency' },
    { icon: Zap, key: 'simplicity' },
    { icon: Heart, key: 'accessibility' },
    { icon: Sparkles, key: 'innovation' }
  ];

  const stats = [
    { value: '10K+', key: 'users' },
    { value: '50K+', key: 'calculations' },
    { value: '5', key: 'tools' },
    { value: '99.9%', key: 'uptime' }
  ];

  return (
    <div className="fade-in">
      {seoContent}

      {/* Mission Section */}
      <section className="bg-[#F9FAFB] py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-orange-100 rounded-full px-4 py-2 mb-6">
                <Target className="h-4 w-4 text-[#EA580C]" />
                <span className="text-sm text-[#EA580C] font-medium">{t('about.mission.badge')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                {t('about.mission.title')}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                  {t('about.mission.description')}
                </p>
                <p className="text-neutral-600 leading-relaxed">
                  {t('about.mission.details')}
                </p>
              </div>
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#EA580C] to-[#C2410C] rounded-xl flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-800">{t('about.mission.highlight.title')}</h3>
                      <p className="text-sm text-neutral-500">{t('about.mission.highlight.subtitle')}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#EA580C] rounded-full" />
                      <span className="text-neutral-600">{t('about.mission.point1')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#EA580C] rounded-full" />
                      <span className="text-neutral-600">{t('about.mission.point2')}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#EA580C] rounded-full" />
                      <span className="text-neutral-600">{t('about.mission.point3')}</span>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#EA580C]/10 rounded-2xl -z-10" />
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-[#EA580C]/20 rounded-xl -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                {t('about.values.title')}
              </h2>
              <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                {t('about.values.subtitle')}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => (
                <div 
                  key={index}
                  className="group p-6 bg-[#F9FAFB] rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#EA580C] to-[#C2410C] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <value.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                    {t(`about.values.${value.key}.title`)}
                  </h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">
                    {t(`about.values.${value.key}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Future Vision Section */}
      <section className="bg-[#F9FAFB] py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <div className="bg-white rounded-2xl shadow-xl p-8 relative">
                  <div className="absolute -top-3 -left-3 w-6 h-6 bg-[#EA580C] rounded-full" />
                  <Bot className="h-16 w-16 text-[#EA580C] mb-6" />
                  <h3 className="text-2xl font-bold text-neutral-800 mb-4">{t('about.future.aiTitle')}</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                      <span className="text-neutral-600">{t('about.future.feature1')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                      <span className="text-neutral-600">{t('about.future.feature2')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                      <span className="text-neutral-600">{t('about.future.feature3')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Sparkles className="h-5 w-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                      <span className="text-neutral-600">{t('about.future.feature4')}</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="order-1 md:order-2">
                <div className="inline-flex items-center gap-2 bg-orange-100 rounded-full px-4 py-2 mb-6">
                  <Sparkles className="h-4 w-4 text-[#EA580C]" />
                  <span className="text-sm text-[#EA580C] font-medium">{t('about.future.badge')}</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6">
                  {t('about.future.title')}
                </h2>
                <p className="text-lg text-neutral-600 mb-6 leading-relaxed">
                  {t('about.future.description')}
                </p>
                <p className="text-neutral-600 leading-relaxed">
                  {t('about.future.aiDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-orange-100 rounded-full px-4 py-2 mb-6">
                <Users className="h-4 w-4 text-[#EA580C]" />
                <span className="text-sm text-[#EA580C] font-medium">{t('about.team.badge')}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
                {t('about.team.title')}
              </h2>
              <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
                {t('about.team.subtitle')}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {teamMembers.map((member) => (
                <div 
                  key={member.id}
                  className="group bg-[#F9FAFB] rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Profile Image */}
                    <div className="flex-shrink-0 mx-auto md:mx-0">
                      <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-white shadow-lg group-hover:shadow-xl transition-shadow">
                        <picture>
                          <source
                            type="image/avif"
                            srcSet={`${member.image.replace('.jpg', '-200w.avif')} 200w, ${member.image.replace('.jpg', '-400w.avif')} 400w`}
                            sizes="128px"
                          />
                          <source
                            type="image/webp"
                            srcSet={`${member.image.replace('.jpg', '-200w.webp')} 200w, ${member.image.replace('.jpg', '-400w.webp')} 400w`}
                            sizes="128px"
                          />
                          <img 
                            src={member.image}
                            srcSet={`${member.image.replace('.jpg', '-200w.jpg')} 200w, ${member.image.replace('.jpg', '-400w.jpg')} 400w`}
                            sizes="128px"
                            alt={t(`about.team.${member.id}.name`)}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover object-top"
                          />
                        </picture>
                      </div>
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 text-center md:text-left">
                      <h3 className="text-xl font-bold text-neutral-800 mb-1">
                        {t(`about.team.${member.id}.name`)}
                      </h3>
                      <p className="text-[#EA580C] font-medium mb-3">
                        {t(`about.team.${member.id}.role`)}
                      </p>
                      <p className="text-neutral-600 text-sm leading-relaxed mb-4">
                        {t(`about.team.${member.id}.description`)}
                      </p>
                      
                      {/* Skills */}
                      <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4">
                        {member.skillKeys.map((skillKey: string, index: number) => (
                          <span 
                            key={index}
                            className="px-3 py-1 bg-white border border-neutral-200 rounded-full text-xs text-neutral-600"
                          >
                            {t(`about.skills.${skillKey}`)}
                          </span>
                        ))}
                      </div>
                      
                      {/* Social Links */}
                      <div className="flex justify-center md:justify-start gap-3">
                        <a 
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-white rounded-lg text-neutral-500 hover:text-[#EA580C] hover:shadow-md transition-all"
                          aria-label="LinkedIn"
                        >
                          <Linkedin className="h-5 w-5" />
                        </a>
                        <a 
                          href={member.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-white rounded-lg text-neutral-500 hover:text-[#EA580C] hover:shadow-md transition-all"
                          aria-label="GitHub"
                        >
                          <Github className="h-5 w-5" />
                        </a>
                        <a 
                          href={member.portfolio}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-white rounded-lg text-neutral-500 hover:text-[#EA580C] hover:shadow-md transition-all"
                          aria-label="Portfolio"
                        >
                          <Globe className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#EA580C] to-[#C2410C] py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('about.cta.title')}
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              {t('about.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate(`/${currentLang}/tools`)}
                className="bg-white text-[#EA580C] hover:bg-orange-50 px-8 py-6 text-lg font-semibold"
              >
                {t('about.cta.button')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .fade-in {
          animation: fadeIn 0.5s ease forwards;
        }
      `}</style>
    </div>
  );
};

export default AboutPage;